<script setup lang="ts">
import { StackLayout } from '@/components/Layouts'
import {
    type OrganizationModel,
    type OrganizationUpdateDataModel
} from '@/types/models/OrganizationModel'
import { ref, watch } from 'vue'
import store from '../../../store'
import { EOrganizationActions } from '../../../store/organizationStore/OrganizationStoreTypes'
import { EStoreModules } from '../../../store/storeType'
import type { PermissionsEnums } from '../../../types/permissions/PermissionsEnum'
import EditableButtonGroup from '../../Buttons/EditableButtonGroup.vue'
import OrganizationAddress from './components/OrganizationAddress.vue'
import OrganizationInformations from './components/OrganizationInformations.vue'

type Props = {
    data: OrganizationModel
    editPermission?: PermissionsEnums
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const isInEditMode = ref(false)
const isLoading = ref(false)

const updatedData = ref<OrganizationUpdateDataModel>({ ...props.data, ...props.data.address })
watch(
    () => props.data,
    newData => (updatedData.value = { ...newData, ...newData.address })
)

const handleChange = (value: Partial<OrganizationUpdateDataModel>) =>
    (updatedData.value = { ...updatedData.value, ...value })

const saveInformations = () => {
    store
        .dispatch(`${EStoreModules.ORGANIZATION}/${EOrganizationActions.UPDATE_ORGANIZATION}`, {
            organizationId: props.data.id,
            organizationData: { ...updatedData.value, address: undefined }
        })
        .then(() => {
            isInEditMode.value = false
            emit('refresh')
        })
        .finally(() => (isLoading.value = false))
}
</script>

<template>
    <StackLayout direction="column" :gap="16" class="full-width">
        <EditableButtonGroup
            :isInEditMode="isInEditMode"
            @edit="isInEditMode = $event"
            @save="saveInformations" />
        <StackLayout direction="row" :gap="8" class="full-width" isResponsiveSmallScreen>
            <OrganizationInformations
                :data="isInEditMode ? updatedData : data"
                :isInEditMode="isInEditMode"
                @change="handleChange"
                class="full-width" />
            <OrganizationAddress
                :address="isInEditMode ? updatedData : data.address"
                :isInEditMode="isInEditMode"
                @change="handleChange"
                class="full-width" />
        </StackLayout>
        <slot />
    </StackLayout>
</template>
